import React from "react";

interface BrandLoaderProps {
  className?: string;
  fullScreen?: boolean;
  label?: string;
}

export function BrandLoader({
  className = "",
  fullScreen = false,
  label = "Loading Waivera",
}: BrandLoaderProps) {
  const containerClassName = [
    "flex flex-col items-center gap-4 text-zinc-950 dark:text-zinc-50",
    fullScreen
      ? "brand-loader-full-screen min-h-[100dvh] w-full justify-center"
      : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={containerClassName} role="status" aria-live="polite">
      <span className="sr-only">{label}</span>
      {/* Your logo with built-in loading animation */}
      <svg
        className="w-auto h-8 max-w-full"
        xmlns="http://www.w3.org/2000/svg"
        width="175"
        height="35"
        viewBox="0 0 175 35"
        fill="none"
      >
        {/* Decorative lines – staggered drawing animation */}
        <path
          d="M1.16211 29.2269H31.6504"
          stroke="currentColor"
          strokeWidth="2.32421"
          strokeLinecap="round"
          className="line"
          pathLength="1"
        />
        <path
          d="M6.21826 33.4943H26.8628"
          stroke="currentColor"
          strokeWidth="2.32421"
          strokeLinecap="round"
          className="line"
          pathLength="1"
          style={{ animationDelay: "0.35s" }}
        />

        {/* Gradient wave – the hero of the loading animation */}
        <path
          d="M1.56982 8.95131C1.56982 8.95131 5.0867 23.4753 9.41609 23.2389C13.7455 23.0026 21.9232 10.2371 20.7206 5.27271C19.518 0.308349 14.467 0.308349 13.505 5.27271C12.5428 10.2371 21.4421 23.4753 24.3284 23.2389C27.2147 23.0026 30.2884 16.6702 31.7846 8.95131"
          stroke="url(#paint0_linear_593_8193)"
          strokeWidth="3.09895"
          strokeLinecap="round"
          fill="none"
          className="wave"
          pathLength="1"
        />

        {/* Text wordmark – gentle breathing pulse */}
        <path
          d="M52.723 26.4008L46.0518 8.4528H49.1337L55.0596 24.7076H53.6035L59.7669 8.4528H62.5099L68.5377 24.7076H67.1153L73.1772 8.4528H76.0895L69.3845 26.4008H66.2689L60.6137 11.5683H61.5616L55.8386 26.4008H52.723ZM89.9673 26.4008V22.608L89.798 21.8968V15.4288C89.798 14.0517 89.3917 12.9906 88.579 12.2456C87.7888 11.478 86.5922 11.0942 84.9893 11.0942C83.9283 11.0942 82.8897 11.2748 81.874 11.636C80.858 11.9747 79.9999 12.4375 79.3004 13.0244L77.9457 10.5862C78.8712 9.8412 79.9775 9.27681 81.2643 8.89304C82.5736 8.48665 83.9395 8.28348 85.3619 8.28348C87.8229 8.28348 89.719 8.88173 91.0512 10.0783C92.383 11.2748 93.0492 13.1035 93.0492 15.5643V26.4008H89.9673ZM84.0751 26.604C82.7429 26.604 81.5692 26.3782 80.5531 25.9267C79.5599 25.4752 78.7921 24.8543 78.2506 24.0642C77.7087 23.2514 77.4375 22.3371 77.4375 21.3212C77.4375 20.3504 77.6633 19.4699 78.115 18.6797C78.5892 17.8896 79.3453 17.2575 80.3838 16.7834C81.4448 16.3093 82.8673 16.0722 84.6507 16.0722H90.34V18.4089H84.7863C83.1609 18.4089 82.0658 18.6798 81.5014 19.2216C80.937 19.7634 80.6546 20.4181 80.6546 21.1857C80.6546 22.0662 81.0048 22.7773 81.7044 23.3191C82.4043 23.8384 83.3751 24.098 84.617 24.098C85.836 24.098 86.897 23.8271 87.8 23.2853C88.7258 22.7435 89.3917 21.9533 89.798 20.9148L90.4415 23.1498C90.0126 24.2109 89.2561 25.0575 88.1726 25.6896C87.0888 26.2992 85.7229 26.604 84.0751 26.604ZM99.0651 26.4008V8.4528H102.316V26.4008H99.0651ZM100.691 4.99863C100.059 4.99863 99.528 4.79546 99.0991 4.38911C98.6928 3.98272 98.4894 3.48604 98.4894 2.89906C98.4894 2.28953 98.6928 1.78154 99.0991 1.37519C99.528 0.968799 100.059 0.765625 100.691 0.765625C101.323 0.765625 101.842 0.968799 102.248 1.37519C102.677 1.75899 102.892 2.24437 102.892 2.83135C102.892 3.44091 102.689 3.96014 102.282 4.38911C101.876 4.79546 101.346 4.99863 100.691 4.99863ZM113.23 26.4008L105.374 8.4528H108.761L115.737 24.7076H114.111L121.223 8.4528H124.406L116.549 26.4008H113.23ZM134.936 26.604C133.017 26.604 131.324 26.2089 129.856 25.4187C128.411 24.6286 127.282 23.5449 126.469 22.1678C125.679 20.7906 125.284 19.2103 125.284 17.4268C125.284 15.6433 125.668 14.0629 126.436 12.6858C127.226 11.3087 128.298 10.2363 129.653 9.46871C131.03 8.67856 132.576 8.28348 134.292 8.28348C136.03 8.28348 137.566 8.66724 138.898 9.43485C140.23 10.2024 141.268 11.2861 142.013 12.6858C142.781 14.0629 143.165 15.6771 143.165 17.5284C143.165 17.6638 143.153 17.8219 143.131 18.0025C143.131 18.1831 143.119 18.3524 143.097 18.5104H127.824V16.1738H141.404L140.083 16.9866C140.106 15.8352 139.869 14.808 139.372 13.9049C138.875 13.0019 138.187 12.302 137.306 11.8054C136.448 11.2861 135.444 11.0265 134.292 11.0265C133.163 11.0265 132.159 11.2861 131.278 11.8054C130.398 12.302 129.709 13.0132 129.212 13.9388C128.716 14.8418 128.467 15.8803 128.467 17.0543V17.5961C128.467 18.7926 128.738 19.865 129.28 20.8132C129.845 21.7388 130.624 22.4613 131.617 22.9805C132.61 23.4998 133.75 23.7594 135.037 23.7594C136.098 23.7594 137.058 23.5788 137.916 23.2176C138.796 22.8564 139.564 22.3145 140.218 21.5921L142.013 23.6917C141.2 24.6399 140.185 25.3623 138.966 25.859C137.769 26.3556 136.426 26.604 134.936 26.604ZM147.679 26.4008V8.4528H150.794V13.3292L150.489 12.1101C150.986 10.8684 151.822 9.92023 152.996 9.26554C154.169 8.61081 155.614 8.28348 157.33 8.28348V11.4328C157.195 11.4103 157.059 11.399 156.924 11.399C156.811 11.399 156.698 11.399 156.585 11.399C154.847 11.399 153.469 11.9182 152.454 12.9567C151.438 13.9952 150.93 15.4966 150.93 17.4607V26.4008H147.679ZM171.155 26.4008V22.608L170.986 21.8968V15.4288C170.986 14.0517 170.58 12.9906 169.767 12.2456C168.977 11.478 167.78 11.0942 166.177 11.0942C165.116 11.0942 164.078 11.2748 163.062 11.636C162.046 11.9747 161.188 12.4375 160.488 13.0244L159.134 10.5862C160.059 9.8412 161.166 9.27681 162.452 8.89304C163.762 8.48665 165.128 8.28348 166.55 8.28348C169.011 8.28348 170.907 8.88173 172.239 10.0783C173.571 11.2748 174.237 13.1035 174.237 15.5643V26.4008H171.155ZM165.263 26.604C163.931 26.604 162.757 26.3782 161.741 25.9267C160.748 25.4752 159.98 24.8543 159.438 24.0642C158.896 23.2514 158.626 22.3371 158.626 21.3212C158.626 20.3504 158.851 19.4699 159.303 18.6797C159.777 17.8896 160.533 17.2575 161.572 16.7834C162.633 16.3093 164.055 16.0722 165.839 16.0722H171.528V18.4089H165.974C164.349 18.4089 163.254 18.6798 162.69 19.2216C162.125 19.7634 161.843 20.4181 161.843 21.1857C161.843 22.0662 162.193 22.7773 162.893 23.3191C163.592 23.8384 164.563 24.098 165.805 24.098C167.024 24.098 168.085 23.8271 168.988 23.2853C169.914 22.7435 170.58 21.9533 170.986 20.9148L171.63 23.1498C171.2 24.2109 170.444 25.0575 169.361 25.6896C168.277 26.2992 166.911 26.604 165.263 26.604Z"
          fill="currentColor"
          className="text-path"
        />

        <defs>
          <linearGradient
            id="paint0_linear_593_8193"
            x1="16.752"
            y1="1.54944"
            x2="16.752"
            y2="23.2421"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0.245234" stopColor="#A640DA" />
            <stop offset="0.567368" stopColor="#FF6FBC" />
            <stop offset="1" stopColor="#FFD45C" />
          </linearGradient>

          {/* All animations are defined here – no external CSS needed! */}
          <style>
            {`
              .wave {
                stroke-dasharray: 1;
                stroke-dashoffset: 1;
                animation: draw 2.1s cubic-bezier(0.4, 0, 0.2, 1) infinite alternate;
              }

              .line {
                stroke-dasharray: 1;
                stroke-dashoffset: 1;
                animation: draw 1.35s cubic-bezier(0.4, 0, 0.2, 1) infinite alternate;
              }

              .text-path {
                animation: pulse 2.4s ease-in-out infinite;
                transform-origin: center;
              }

              @keyframes draw {
                to {
                  stroke-dashoffset: 0;
                }
              }

              @keyframes pulse {
                0%, 100% {
                  opacity: 0.92;
                  transform: scale(0.985);
                }
                50% {
                  opacity: 1;
                  transform: scale(1.015);
                }
              }
            `}
          </style>
        </defs>
      </svg>

      {/* Brand-colored loading dots (Tailwind only) */}
      <div className="flex gap-1.5">
        <div
          className="h-1.5 w-1.5 rounded-full bg-[#A640DA] animate-bounce"
          style={{ animationDelay: "0s" }}
        />
        <div
          className="h-1.5 w-1.5 rounded-full bg-[#FF6FBC] animate-bounce"
          style={{ animationDelay: "0.15s" }}
        />
        <div
          className="h-1.5 w-1.5 rounded-full bg-[#FFD45C] animate-bounce"
          style={{ animationDelay: "0.3s" }}
        />
      </div>
    </div>
  );
};
